// @ts-check

import { Dummy } from "mibaby-frontend/media/Image";
import {
  getTimeoutDisplay,
  TIMEOUT_FUTURE,
  TIMEOUT_PASSED,
  TIMEOUT_SOON,
  TIMEOUT_URGENT,
  toEuro,
} from "mibaby-frontend/Offer";
import { DEFAULT_SOCIAL_BUTTON } from "mibaby-frontend/social/VoteBar";
import useUrl, { setQuery } from "mibaby-frontend/util/hooks/useUrl";
import { parseUrl, useNativeShare } from "mibaby-frontend/util/social-share";
import React from "react";
import Modal from "react-bootstrap/lib/Modal";
import { withProps } from "recompose";
import ClickTracker from "@components/click-tracker";
import { CommentContainer } from "@components/comments";
import { HtmlToReact } from "@components/html";
import { VoteBarContainer } from "@components/VoteBar";
import { splitId } from "@components/content-feed/admin/manager";
import Asset from "./Asset";
import feedTeaserFactory from "./factory";
import Link, { VIEW_ACTION_URL } from "./Link";
import { Body } from "@components/Teaser/Panel";

/**
 * @return {[any[], typeof DEFAULT_SOCIAL_BUTTON]}
 */
function getSocialButtons(social_buttons = []) {
  return [
    // get all remaining buttons
    social_buttons.filter(t => t.type !== "UPDOWNVOTE" && t.type !== "LIKE"),
    // get the updownvoter
    social_buttons.find(t => t.type === "UPDOWNVOTE"),
  ];
}

/**
 * @typedef {{
 *   onClick: (e: React.SyntheticEvent) => void
 *   isActive: boolean
 * } & React.ButtonHTMLAttributes<HTMLButtonElement>} VoteButtonProps
 */

const UntilDateListGroupItem = ({ until, className = "", state = "default", ...props }) => (
  <div {...props} className={`list-group-item list-group-item-${state} ${className}`}>
    {until}
  </div>
);

/**
 * VoteBarContainer usable from within a Teaser, auto-saving user selection to server
 * @param {{
 *  className?: string,
 *  id: string,
 *  sum: number,
 *  count: number,
 *  ups:number,
 *  downs:number,
 *  userLike: import("@components/VoteBar").VOTE_ENUM,
 *  fakeCount: number,
 *  handleChange?: Function
 * }} param0
 */
export const TeaserVoteBarContainer = ({
  id,
  sum,
  count,
  downs,
  ups,
  userLike,
  fakeCount = 0,
  handleChange: forwardHandleChange = null,
  ...props
}) => {
  const [, curId] = id.split("_");
  // @ts-ignore
  const pvid = window["miBaby"] && miBaby.pvid;
  const handleChange = (e, newState) => {
    const method = newState.selected === 0 ? "DELETE" : "POST";
    const path = newState.selected === -1 ? "dislike" : "like";
    fetch(`/content_feeds/${path}/${curId}?pvid=${pvid}`, { method });
    forwardHandleChange && forwardHandleChange(newState);
    e.preventDefault();
    e.stopPropagation();
  };
  return (
    <VoteBarContainer
      initialSum={fakeCount + sum}
      initialSelected={userLike}
      initialUps={fakeCount + ups}
      initialDowns={downs}
      onChange={handleChange}
      {...props}
    />
  );
};

export const PriceDisplay = ({ price, comparePrice = 0, shopName, shopImg }) => (
  <>
    <span className="text-primary price-current">{toEuro(price / 100)}</span>
    {comparePrice > 0 && (
      <>
        {` `}
        <del className="text-muted price-compare">{toEuro(comparePrice / 100)}</del>
        {` `}
        <span className="price-difference">
          (-{((1 - price / comparePrice) * 100).toFixed(1)}%)
        </span>
      </>
    )}
    {shopName && (
      <>
        {` `}
        <Dummy
          src={shopImg}
          className="price-shop"
          fallback={<span className="text-muted price-shop">bei {shopName}</span>}
          style={{ maxHeight: "25px" }}
        />
      </>
    )}
  </>
);

/**
 * @param {import("..").CommonTeaserProps & {
 *  id: string,
 *  compare_price: number,
 *  teaser_id: string,
 *  price: number,
 *  url: string,
 *  valid_from: string,
 *  valid_until: string,
 *  shop_name?: string,
 *  shop_img?: string,
 *  content: string,
 *  onHide: () => void
 * }} param0
 */
export const DealDetailModal = ({
  onHide,
  id,
  teaser_id,
  content,
  title,
  url,
  price,
  valid_until,
  compare_price,
  shop_name,
  shop_img,
  asset_mimetype,
  asset_versions,
  social_buttons = [],
  AGG_LIKE = { count: 0, sum: 0, ups: 0, downs: 0, userLike: 0 },
}) => {
  const redirectUrl = useUrl(true, { top: teaser_id });
  const [, contentItemId] = typeof id === "string" && id.split("_");
  const [, teaserId] = typeof teaser_id === "string" && teaser_id.split("_");
  const [, updownVote] = ([social_buttons] = getSocialButtons(social_buttons));
  const comment = social_buttons.find(item => item.type === "COMMENT");

  const [showShare, handleShare] = useNativeShare({
    url: () => parseUrl(setQuery(document.location.href, { top: teaserId })),
  });
  const [timeoutPriority, timeoutContent] = getTimeoutDisplay(valid_until);

  return (
    <Modal
      container={document.getElementById("content")}
      show={true}
      onHide={onHide}
      className="modal-mobile-fullscreen"
      autoFocus={false}
      enforceFocus={false}
      restoreFocus={false}
    >
      <div
        className={`modal-body-overflow-x modal-content-condensed modal-deal${
          timeoutPriority === TIMEOUT_PASSED ? " filter-grayscale" : ""
        }`}
      >
        <div className="clearfix">
          {showShare && (
            <button className="btn btn-link pull-right" onClick={handleShare}>
              Teilen{` `}
              <i className="glyphicon glyphicon-share" />
            </button>
          )}
          <button className="btn btn-link pull-left" onClick={onHide}>
            <i className="glyphicon glyphicon-chevron-left" />
            {` `}
            Zurück
          </button>
        </div>
        <Modal.Body>
          <div className="modal-deal-header-image">
            <Asset
              teaserId={id}
              asset_mimetype={asset_mimetype}
              asset_versions={asset_versions}
              imageProps={{ responsive: false, size: "w320" }}
            />
          </div>

          <div className="list-group">
            {updownVote && (
              <div className="list-group-item">
                <TeaserVoteBarContainer id={id} {...AGG_LIKE} fakeCount={updownVote.fakeCount} />
              </div>
            )}
            {price > 0 && (
              <div className="list-group-item text-center price-display">
                <PriceDisplay
                  comparePrice={compare_price}
                  price={price}
                  shopImg={shop_img}
                  shopName={shop_name}
                />
              </div>
            )}
            {timeoutPriority && (
              <UntilDateListGroupItem
                state={
                  timeoutPriority & TIMEOUT_URGENT
                    ? "danger"
                    : timeoutPriority & TIMEOUT_SOON
                    ? "warning"
                    : timeoutPriority & TIMEOUT_PASSED
                    ? "default"
                    : ""
                }
                className="valid-until text-center"
                until={timeoutContent}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Header>{title}</Modal.Header>
        <Modal.Body>
          <div className="content-feed-link text-center hidden-xs hidden-sm">
            <ClickTracker name="feed_checkpoint" aid={contentItemId}>
              <a href={url} className="btn btn-primary btn-rounded">
                Zum Deal <i className="glyphicon glyphicon-menu-right" />
              </a>
            </ClickTracker>
          </div>

          <HtmlToReact html={content} preset="basic" />
        </Modal.Body>
        {comment && (
          <CommentContainer
            modelType="ContentTeaser"
            modelId={splitId(teaser_id)}
            lightboxId={comment.registerLightboxId || undefined}
            lightboxArgs={{
              activation_redirect: redirectUrl,
            }}
          />
        )}
      </div>
      <Modal.Footer className="visible-xs visible-sm">
        <div className="content-feed-link text-center">
          <ClickTracker name="feed_checkpoint" aid={contentItemId}>
            <a href={url} className="btn btn-primary btn-rounded">
              Zum Deal <i className="glyphicon glyphicon-menu-right" />
            </a>
          </ClickTracker>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * @param {import("..").CommonTeaserProps & {
 *  id: string,
 *  teaserId: string,
 *  compare_price: number,
 *  price: number,
 *  url: string,
 *  valid_from: string,
 *  valid_until: string,
 *  shop_name?: string,
 *  shop_img?: string,
 *  trivias?: array
 * }} param0
 */
export const DealBody = ({ compare_price, price, valid_until, shop_name, shop_img, ...props }) => {
  const [timeoutPriority, timeoutContent] = getTimeoutDisplay(valid_until);

  return (
    <>
      <Body {...props} />
      <div className="list-group">
        {(timeoutPriority & TIMEOUT_URGENT) === TIMEOUT_URGENT && (
          <UntilDateListGroupItem state="danger" className="valid-until" until={timeoutContent} />
        )}
        {price > 0 && (
          <div className="list-group-item text-center price-display">
            <PriceDisplay
              comparePrice={compare_price}
              price={price}
              shopImg={shop_img}
              shopName={shop_name}
            />
          </div>
        )}
      </div>
    </>
  );
};

export const Deal = feedTeaserFactory({
  displayName: "Deal",
  className: "content-feed-teaser content-feed-deal",
  Content: withProps(({ teaserId }) => ({
    view_action: VIEW_ACTION_URL,
    url: `#/deal/${teaserId}`,
  }))(Link),
  Body: DealBody,
});

export default Deal;
